/**
 * Shared print utilities for barcode labels and tables.
 * Used by MaterialStatusDashboard, Product page, and JobDetail modal.
 */

export interface BarcodePrintItem {
  barcode: string;
  name: string;
  productName?: string | null;
  room?: string | null;
  address?: string | null;
  productTypeName?: string | null;
  width?: number | null;
  height?: number | null;
}

function formatInches(value: number): string {
  const whole = Math.floor(value);
  const fracOf16 = Math.round((value - whole) * 16);
  const fractionMap: Record<number, string> = {
    1: '1/16', 2: '1/8', 3: '3/16', 4: '1/4', 5: '5/16',
    6: '3/8', 7: '7/16', 8: '1/2', 9: '9/16', 10: '5/8',
    11: '11/16', 12: '3/4', 13: '13/16', 14: '7/8', 15: '15/16',
  };
  const fracStr = fractionMap[fracOf16] || '';
  if (!fracStr) return `${whole}"`;
  return whole > 0 ? `${whole} ${fracStr}"` : `${fracStr}"`;
}

function formatSize(width?: number | null, height?: number | null): string | null {
  if (width && height) return `${formatInches(width)} X ${formatInches(height)}`;
  if (width) return formatInches(width);
  if (height) return formatInches(height);
  return null;
}

export interface BarcodeTableItem {
  jobName?: string | null;
  productName?: string | null;
  room?: string | null;
  size?: string | null;
  barcode?: string | null;
  typeOfWork?: string | null;
}

export interface BarcodePrintOptions {
  title?: string;
  /** JsBarcode width (default: 2) */
  barcodeWidth?: number;
  /** JsBarcode height (default: 100) */
  barcodeHeight?: number;
  /** Show barcode value text below barcode (default: false) */
  displayValue?: boolean;
  /** Font size for barcode value text (default: 14) */
  barcodeFontSize?: number;
}

function openPrintWindow(errorMessage?: string): Window | null {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert(errorMessage || 'Please allow popups to print');
  }
  return printWindow;
}

function writePrintWindow(printWindow: Window, html: string) {
  printWindow.document.write(html);
  printWindow.document.close();
}

const JSBARCODE_CDN = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';

const PRINT_SCRIPT = `
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
        window.onafterprint = function() {
          window.close();
        };
      }, 500);
    };
  <\/script>
`;

/**
 * Print barcode labels — one barcode per page.
 * Each label shows: name, room, address, product type, and barcode.
 */
export function printBarcodes(
  items: BarcodePrintItem[],
  options: BarcodePrintOptions = {}
): boolean {
  const barcodes = items.filter((item) => item.barcode);

  if (barcodes.length === 0) {
    alert('No barcodes found to print');
    return false;
  }

  const printWindow = openPrintWindow('Please allow popups to print barcodes');
  if (!printWindow) return false;

  const {
    title = 'Print Barcodes',
    barcodeWidth = 2,
    barcodeHeight = 100,
    displayValue = false,
    barcodeFontSize = 14,
  } = options;

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          @media print {
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              margin: 0;
              padding: 0;
            }
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .barcode-container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100vh;
            text-align: center;
            page-break-after: always;
            box-sizing: border-box;
            padding: 20px;
          }
          .barcode-container:last-child {
            page-break-after: auto;
          }
          .barcode-name {
            margin-bottom: 10px;
            font-weight: bold;
            font-size: 28px;
          }
          .barcode-product-name {
            margin-bottom: 8px;
            font-size: 22px;
            font-weight: bold;
          }
          .barcode-room {
            margin-bottom: 8px;
            font-size: 22px;
          }
          .barcode-address {
            margin-bottom: 8px;
            font-weight: bold;
            font-size: 22px;
          }
          .barcode-product-type {
            margin-bottom: 15px;
            font-size: 22px;
            font-style: italic;
          }
          .barcode-image {
            max-width: 80%;
          }
          .barcode-size {
            margin-top: 10px;
            font-size: 24px;
            font-weight: bold;
          }
        </style>
        <script src="${JSBARCODE_CDN}"><\/script>
      </head>
      <body>
        ${barcodes
          .map(
            (item, index) => `
          <div class="barcode-container">
            <div class="barcode-name">${item.name}</div>
            ${item.productName ? `<div class="barcode-product-name">Product Name: ${item.productName}</div>` : ''}
            ${item.room ? `<div class="barcode-room">Room: ${item.room}</div>` : ''}
            ${item.address ? `<div class="barcode-address">${item.address}</div>` : ''}
            ${item.productTypeName ? `<div class="barcode-product-type">(${item.productTypeName})</div>` : ''}
            <svg id="barcode-${index}" class="barcode-image"></svg>
            ${formatSize(item.width, item.height) ? `<div class="barcode-size">${formatSize(item.width, item.height)}</div>` : ''}
          </div>
          <script>
            JsBarcode("#barcode-${index}", "${item.barcode}", {
              format: "CODE128",
              width: ${barcodeWidth},
              height: ${barcodeHeight},
              displayValue: ${displayValue},
              fontSize: ${barcodeFontSize}
            });
          <\/script>
        `
          )
          .join('')}
        ${PRINT_SCRIPT}
      </body>
    </html>
  `;

  writePrintWindow(printWindow, html);
  return true;
}

/**
 * Print a single barcode label (full page).
 */
export function printSingleBarcode(
  item: BarcodePrintItem,
  options: BarcodePrintOptions = {}
): boolean {
  return printBarcodes([item], options);
}

/**
 * Print a table with embedded barcodes (landscape).
 * Used by MaterialStatusDashboard for table view.
 */
export function printBarcodeTable(
  items: BarcodeTableItem[],
  options: { title?: string } = {}
): boolean {
  const printWindow = openPrintWindow('Please allow popups to print table');
  if (!printWindow) return false;

  const { title = 'Material Status' } = options;

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>
          @media print {
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            body { margin: 0; padding: 0; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }
          h2 {
            margin-bottom: 15px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #333;
            padding: 8px 10px;
            text-align: left;
            font-size: 13px;
          }
          th {
            background-color: #f0f0f0;
            font-weight: bold;
          }
          tr:nth-child(even) {
            background-color: #fafafa;
          }
          .barcode-cell {
            text-align: center;
          }
          .barcode-cell svg {
            max-width: 160px;
            height: auto;
          }
        </style>
        <script src="${JSBARCODE_CDN}"><\/script>
      </head>
      <body>
        <h2>${title}</h2>
        <table>
          <thead>
            <tr>
              <th>Job Name</th>
              <th>Product</th>
              <th>Room</th>
              <th>Size</th>
              <th>Type of Work</th>
              <th>Barcode</th>
            </tr>
          </thead>
          <tbody>
            ${items
              .map(
                (item, index) => `
              <tr>
                <td>${item.jobName || '-'}</td>
                <td>${item.productName || '-'}</td>
                <td>${item.room || '-'}</td>
                <td>${item.size || '-'}</td>
                <td>${item.typeOfWork || '-'}</td>
                <td class="barcode-cell">
                  ${item.barcode ? `<svg id="barcode-${index}"></svg><br/><span style="font-size:10px">${item.barcode}</span>` : '-'}
                </td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
        <script>
          ${items
            .map((item, index) =>
              item.barcode
                ? `JsBarcode("#barcode-${index}", "${item.barcode}", {
                    format: "CODE128",
                    width: 1.5,
                    height: 40,
                    displayValue: false
                  });`
                : ''
            )
            .join('\n')}
        <\/script>
        ${PRINT_SCRIPT}
      </body>
    </html>
  `;

  writePrintWindow(printWindow, html);
  return true;
}
